import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  ButtonBase,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Calendar, TrendingDown, TrendingUp } from "lucide-react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getAnalyticsOverviewAPI,
  getSharesAnalyticsAPI,
  getViewsAnalyticsAPI,
} from "../../../../api/analyticsApi";

type Period = "week" | "month";

type EngagementRow = {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
};

type OverviewData = {
  views?: { total?: number; change?: number };
  likes?: { total?: number; change?: number };
  comments?: { total?: number; change?: number };
  shares?: { total?: number; change?: number };
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    marginBottom: "30px",
    padding: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    gap: "16px",
    flexWrap: "wrap",
    backgroundColor: "transparent",
  },
  title: {
    color: "#FFFFFF",
    fontSize: "24px",
    fontWeight: 700,
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  periodSelect: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 400,
    minWidth: "140px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-select": {
      color: "#FFFFFF",
      fontSize: "16px",
      fontWeight: 400,
      padding: "8px 32px 8px 36px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-icon": {
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      "& .MuiSelect-select": {
        fontSize: "12px",
      },
    },
  },
  periodSelectIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
  },
  statsRow: {
    display: "flex",
    flexWrap: "nowrap",
    gap: "20px",
    marginBottom: "24px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      gap: "8px",
      marginBottom: "16px",
    },
  },
  statCard: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "8px",
    minHeight: "auto",
    cursor: "default",
    borderRadius: "12px",
    backgroundColor: "#161514 !important",
    border: "none !important",
    textAlign: "left",
    font: "inherit",
    color: "inherit",
    outline: "none",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: "#161514 !important",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "#161514 !important",
    },
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 0",
      minWidth: 0,
      padding: "8px",
      borderRadius: "10px",
    },
  },
  contentPanel: {
    backgroundColor: "#1F1F1FCC",
    borderRadius: "12px",
    padding: 0,
    overflow: "hidden",
  },
  statLabel: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: "6px",
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
      fontWeight: 400,
    },
  },
  statValue: {
    color: "#FFFFFF",
    fontSize: "24px",
    fontWeight: 500,
    lineHeight: 1.2,
    marginBottom: "6px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
    },
  },
  statTrend: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  trendPositive: {
    color: "#73C75C",
  },
  trendNegative: {
    color: "#B86483",
  },
  chartSection: {
    padding: "24px 24px 28px",
    boxSizing: "border-box",
    border: "none",
    backgroundColor: "#1b1b1b",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px 20px",
    },
  },
  chartTitle: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "20px",
    lineHeight: 1.25,
  },
  chartWrap: {
    height: "300px",
    width: "100%",
    padding: 0,
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      height: "240px",
    },
  },
  chartLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#FFFFFF",
  },
  menuPaper: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #363636",
  },
  menuItem: {
    color: "#FFFFFF",
    fontSize: "14px",
  },
  tooltipDate: {
    color: "#FFFFFF",
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "8px",
  },
  tooltipRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#FFFFFF",
    marginBottom: "4px",
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
}));

const formatCount = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatDateLabel = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const sortByDate = (rows: EngagementRow[]) =>
  [...rows].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

const filterRowsByPeriod = (rows: EngagementRow[], period: Period) => {
  const sorted = sortByDate(rows);
  if (period === "week") return sorted.slice(-7);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return sorted.filter(row => {
    const d = new Date(row.date);
    return d.getFullYear() === year && d.getMonth() === month;
  });
};

const normalizeSeries = (raw: unknown): { date: string; count: number }[] => {
  if (!raw) return [];
  const arr =
    (raw as { data?: unknown[] })?.data ??
    (raw as { timeline?: unknown[] })?.timeline ??
    (raw as { items?: unknown[] })?.items ??
    (Array.isArray(raw) ? raw : []);

  return (arr as Record<string, unknown>[])
    .map(item => ({
      date: String(item.date ?? item.timestamp ?? item.day ?? ""),
      count: Number(
        item.count ?? item.total ?? item.views ?? item.shares ?? item.value ?? 0
      ),
    }))
    .filter(item => item.date);
};

const mergeEngagementSeries = (
  viewsSeries: { date: string; count: number }[],
  sharesSeries: { date: string; count: number }[],
  likesSeries: { date: string; count: number }[],
  commentsSeries: { date: string; count: number }[]
): EngagementRow[] => {
  const dateMap = new Map<string, EngagementRow>();

  const upsert = (
    date: string,
    patch: Partial<EngagementRow>
  ) => {
    const key = date.slice(0, 10);
    const existing = dateMap.get(key) ?? {
      date: key,
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
    };
    dateMap.set(key, { ...existing, ...patch, date: key });
  };

  viewsSeries.forEach(({ date, count }) =>
    upsert(date, { views: count })
  );
  sharesSeries.forEach(({ date, count }) =>
    upsert(date, { shares: count })
  );
  likesSeries.forEach(({ date, count }) =>
    upsert(date, { likes: count })
  );
  commentsSeries.forEach(({ date, count }) =>
    upsert(date, { comments: count })
  );

  return sortByDate(Array.from(dateMap.values()));
};

const EngagementTooltip: React.FC<{
  active?: boolean;
  payload?: ReadonlyArray<{
    payload?: EngagementRow & { label?: string };
  }>;
  label?: string;
  classes: ReturnType<typeof useStyles>;
}> = ({ active, payload, label, classes }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const displayDate =
    label != null
      ? formatDateLabel(String(label))
      : formatDateLabel(row.date);

  return (
    <Box
      style={{
        background: "#0b0b0f",
        border: "1px solid #363636",
        borderRadius: 8,
        padding: "12px",
      }}
    >
      <Typography className={classes.tooltipDate}>{displayDate}</Typography>
      {[
        { key: "Views", value: row.views, color: "#A78BFA" },
        { key: "Likes", value: row.likes, color: "#FE5A97" },
        { key: "Shares", value: row.shares, color: "#60A5FA" },
      ].map(item => (
        <Box key={item.key} className={classes.tooltipRow}>
          <span
            className={classes.tooltipDot}
            style={{ backgroundColor: item.color }}
          />
          {item.key}: {formatCount(item.value)}
        </Box>
      ))}
    </Box>
  );
};

const ContentAnalyticsSection: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [period, setPeriod] = useState<Period>("week");
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [engagementRows, setEngagementRows] = useState<EngagementRow[]>([]);
  const [loading, setLoading] = useState(true);

  const periodLabel = period === "week" ? "last week" : "last month";
  const days = period === "week" ? 7 : 30;

  const stats = useMemo(
    () => [
      {
        key: "views" as const,
        label: "Total views",
        value: overview?.views?.total ?? 0,
        change: overview?.views?.change,
      },
      {
        key: "likes" as const,
        label: "Total Likes",
        value: overview?.likes?.total ?? 0,
        change: overview?.likes?.change,
      },
      {
        key: "comments" as const,
        label: "Total Comments",
        value: overview?.comments?.total ?? 0,
        change: overview?.comments?.change,
      },
      {
        key: "shares" as const,
        label: "Total Shares",
        value: overview?.shares?.total ?? 0,
        change: overview?.shares?.change,
      },
    ],
    [overview]
  );

  const chartData = useMemo(
    () =>
      filterRowsByPeriod(engagementRows, period).map(row => ({
        ...row,
        label: formatDateLabel(row.date),
      })),
    [engagementRows, period]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [overviewRes, viewsRes, sharesRes] = await Promise.all([
          getAnalyticsOverviewAPI({ days }),
          getViewsAnalyticsAPI({ days }).catch(() => null),
          getSharesAnalyticsAPI({ days }).catch(() => null),
        ]);

        if (cancelled) return;

        const overviewPayload =
          (overviewRes as { overview?: OverviewData })?.overview ??
          (overviewRes as OverviewData);

        setOverview({
          views: {
            total: overviewPayload?.views?.total ?? 0,
            change: (overviewPayload as { views?: { change?: number } })?.views
              ?.change,
          },
          likes: {
            total:
              overviewPayload?.likes?.total ??
              (overviewPayload as { reactions?: { likes?: number } })?.reactions
                ?.likes ??
              0,
            change: overviewPayload?.likes?.change,
          },
          comments: {
            total: overviewPayload?.comments?.total ?? 0,
            change: overviewPayload?.comments?.change,
          },
          shares: {
            total: overviewPayload?.shares?.total ?? 0,
            change: overviewPayload?.shares?.change,
          },
        });

        const viewsSeries = normalizeSeries(viewsRes);
        const sharesSeries = normalizeSeries(sharesRes);
        const likesFromOverview = normalizeSeries(
          (overviewRes as { likesTimeline?: unknown })?.likesTimeline
        );
        const commentsFromOverview = normalizeSeries(
          (overviewRes as { commentsTimeline?: unknown })?.commentsTimeline
        );

        setEngagementRows(
          mergeEngagementSeries(
            viewsSeries,
            sharesSeries,
            likesFromOverview,
            commentsFromOverview
          )
        );
      } catch {
        if (!cancelled) {
          setOverview(null);
          setEngagementRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [period, days]);

  const renderTrend = (change?: number) => {
    const trendValue =
      change === undefined || change === null || Number.isNaN(change)
        ? 0
        : change;
    const isPositive = trendValue >= 0;
    const TrendIcon = isPositive ? TrendingUp : TrendingDown;
    const sign = isPositive ? "+" : "";
    return (
      <Typography
        component="span"
        className={`${classes.statTrend} ${
          isPositive ? classes.trendPositive : classes.trendNegative
        }`}
      >
        <TrendIcon size={14} />
        {sign}
        {Math.abs(trendValue).toFixed(0)}% than {periodLabel}
      </Typography>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Content Analytics</Typography>
        <Box position="relative">
          <Box className={classes.periodSelectIcon}>
            <Calendar size={16} color="#FFFFFF" />
          </Box>
          <Select
            value={period}
            onChange={e => setPeriod(e.target.value as Period)}
            variant="outlined"
            className={classes.periodSelect}
            MenuProps={{ classes: { paper: classes.menuPaper } }}
          >
            <MenuItem value="week" className={classes.menuItem}>
              This week
            </MenuItem>
            <MenuItem value="month" className={classes.menuItem}>
              This month
            </MenuItem>
          </Select>
        </Box>
      </Box>

      <Box className={classes.statsRow}>
        {stats.map(stat => (
          <ButtonBase
            key={stat.key}
            disableRipple
            className={classes.statCard}
            tabIndex={-1}
          >
            <Typography className={classes.statLabel}>{stat.label}</Typography>
            <Typography className={classes.statValue}>
              {loading ? "..." : formatCount(stat.value)}
            </Typography>
            {renderTrend(stat.change)}
          </ButtonBase>
        ))}
      </Box>

      <Box className={classes.contentPanel}>
        <Box className={classes.chartSection}>
          <Typography className={classes.chartTitle}>
            Engagement, users
          </Typography>
          <Box className={classes.chartWrap}>
            {loading ? (
              <Box className={classes.chartLoading}>
                <CircularProgress size={28} style={{ color: "#FE5A97" }} />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 12, right: 12, left: -16, bottom: 4 }}
                >
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#FFFFFF", fontSize: isMobile ? 11 : 12 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    dy={8}
                  />
                  <YAxis
                    tick={{ fill: "#FFFFFF", fontSize: isMobile ? 11 : 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={isMobile ? 28 : 32}
                    tickMargin={2}
                    allowDecimals={false}
                    orientation="left"
                    tickFormatter={v => formatCount(Number(v))}
                  />
                  <Tooltip
                    content={
                      <EngagementTooltip classes={classes} />
                    }
                    cursor={{
                      stroke: "rgba(255,255,255,0.35)",
                      strokeWidth: 1,
                      strokeDasharray: "4 4",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="views"
                    stroke="#A78BFA"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#A78BFA" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="likes"
                    stroke="#FE5A97"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#FE5A97" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="shares"
                    stroke="#60A5FA"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#60A5FA" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ContentAnalyticsSection;
